import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights, IConfig, IConfiguration, SeverityLevel } from '@microsoft/applicationinsights-web';

import { MsalService } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { IAppInsightsHttpDetails } from './appInsights.models';


@Injectable({
    providedIn: 'root'
})
export class AppInsightsService {

    private _instrumentationKey: string;
    private _instrumentationClientName: string;
    private _extraConfiguration: IConfiguration & IConfig;

    angularPlugin: AngularPlugin;
    appInsights: ApplicationInsights;

    setConfig(instrumentationKey: string, instrumentationClientName: string, extraConfiguration: IConfiguration & IConfig) {
        this._instrumentationKey = instrumentationKey;
        this._instrumentationClientName = instrumentationClientName;
        this._extraConfiguration = extraConfiguration;

        this.initialize();
    }

    constructor(
        private readonly router: Router,
        private readonly msalService: MsalService) {
    }

    private initialize() {
        const angularPlugin = new AngularPlugin();
        const config = {
            instrumentationKey: this._instrumentationKey,
            ...this._extraConfiguration,
            extensions: [angularPlugin],
            extensionConfig: {
                [angularPlugin.identifier]: { router: this.router }
            }
        };

        this.appInsights = new ApplicationInsights({ config });

        const account = this.msalService.instance.getActiveAccount();
        const username = account?.idTokenClaims?.['preferred_username'] || null;

        if (username) {
            this.appInsights.setAuthenticatedUserContext(username);
        }

        const slotName = this._instrumentationClientName ?? 'Lynk360-WebApp';

        this.appInsights.addTelemetryInitializer((envelope) => {
          envelope.tags = envelope.tags ?? {};
          envelope.tags['ai.cloud.role'] = slotName;
          envelope.tags['ai.application.ver'] = environment.appInsights.buildNumber;
          envelope.tags["ai.user.id"] = username ?? 'anonymous';

          envelope.data ??= {};
          envelope.data.properties ??= {};
          envelope.data.properties['slotName'] = slotName;
        });

        this.appInsights.loadAppInsights();
    }

    logPageView(name: string, url?: string, properties?: any, measurements?: any, duration?: number) {
        try {
            this.appInsights.trackPageView({ name, uri: url, measurements, properties: { ...properties, duration } });
        }
        catch { console.log("trackPageView"); }
    }

    logEvent(name: string, properties?: any, measurements?: any) {
        try {
            this.appInsights.trackEvent({ name }, { measurements, properties });
        }
        catch { console.log("trackEvent"); }
    }

    logException(exception: Error, handledAt?: any, properties?: any, measurements?: any, severityLevel?: SeverityLevel) {
        try {
            this.appInsights.trackException({ exception, severityLevel }, { handledAt, properties, measurements });
        }
        catch {
            console.log("logException");
        }
    }

    logHttpException(exception: Error, callerService: string, requestDetails: IAppInsightsHttpDetails, measurements?: any, severityLevel?: SeverityLevel) {
        try {
            const details = { ...requestDetails, payload: (requestDetails.payload ? JSON.stringify(requestDetails.payload || {}) : undefined) };
            this.logException(exception, callerService, details, measurements, severityLevel);
        }
        catch { console.log("logException"); }
    }
}
